<template>
  <div class="flex h-screen overflow-hidden">
    <!-- Sidebar -->
    <aside
      class="sticky top-0 flex flex-col items-center h-screen space-y-4 border-r-2 w-72 bg-base-200 border-secondary-accent"
      aria-label="Sidebar"
    >
      <!-- Logo -->
      <span class="grid w-full m-5 h-18 place-content-center">
        <img
          src="https://cloud.rana.ai/static/images/logoName.png"
          alt="Logo"
          class="object-cover w-full"
        />
      </span>

      <!-- Navigation -->
      <ul class="w-full menu menu-lg">
        <li class="flex flex-col items-center my-4 text-teal-500">
          <NuxtLink
            to="/cloud/profile"
            class="flex flex-col items-center text-teal-500 hover:text-teal-400 focus:outline-none"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              class="w-12 h-12 mb-2"
            >
              <path
                fill="evenodd"
                d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z"
              />
            </svg>
            <span class="text-lg font-medium">Profile</span>
          </NuxtLink>
        </li>

        <li class="flex flex-col items-center my-4 text-teal-500">
          <NuxtLink
            to="/cloud/subscriptions"
            class="flex flex-col items-center text-teal-500 hover:text-teal-400 focus:outline-none"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              class="w-12 h-12 mb-2"
            >
              <path
                fill-rule="evenodd"
                d="M9 4.5a.75.75 0 0 1 .721.544l.813 2.846a3.75 3.75 0 0 0 2.576 2.576l2.846.813a.75.75 0 0 1 0 1.442l-2.846.813a3.75 3.75 0 0 0-2.576 2.576l-.813 2.846a.75.75 0 0 1-1.442 0l-.813-2.846a3.75 3.75 0 0 0-2.576-2.576l-2.846-.813a.75.75 0 0 1 0-1.442l2.846-.813A3.75 3.75 0 0 0 7.466 7.89l.813-2.846A.75.75 0 0 1 9 4.5ZM18 1.5a.75.75 0 0 1 .728.568l.258 1.036c.236.94.97 1.674 1.91 1.91l1.036.258a.75.75 0 0 1 0 1.456l-1.036.258c-.94.236-1.674.97-1.91 1.91l-.258 1.036a.75.75 0 0 1-1.456 0l-.258-1.036a2.625 2.625 0 0 0-1.91-1.91l-1.036-.258a.75.75 0 0 1 0-1.456l1.036-.258a2.625 2.625 0 0 0 1.91-1.91l.258-1.036A.75.75 0 0 1 18 1.5ZM16.5 15a.75.75 0 0 1 .712.513l.394 1.183c.15.447.5.799.948.948l1.183.395a.75.75 0 0 1 0 1.422l-1.183.395c-.447.15-.799.5-.948.948l-.395 1.183a.75.75 0 0 1-1.422 0l-.395-1.183a1.5 1.5 0 0 0-.948-.948l-1.183-.395a.75.75 0 0 1 0-1.422l1.183-.395c.447-.15.799-.5.948-.948l.395-1.183A.75.75 0 0 1 16.5 15Z"
                clip-rule="evenodd"
              />
            </svg>
            <span class="text-lg font-medium">Subscriptions</span>
          </NuxtLink>
        </li>

        <li class="flex flex-col items-center my-4 text-teal-500">
          <a
            :href="sessionUrl"
            target="_blank"
            class="flex flex-col items-center text-teal-500 hover:text-teal-400 focus:outline-none"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-12 h-12"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 0 0 2.25-2.25V6.75a2.25 2.25 0 0 0-2.25-2.25H6.75A2.25 2.25 0 0 0 4.5 6.75v10.5a2.25 2.25 0 0 0 2.25 2.25Zm.75-12h9v9h-9v-9Z"
              />
            </svg>
            <span class="text-lg font-medium">Rana Engine</span>
          </a>
        </li>
      </ul>

      <!-- Logout Button -->
      <div class="mb-4">
        <Button
          @click="logout()"
          class="h-12 border-2 fill-accent hover:fill-accent-content border-accent"
          variant="outline"
        >
          <!-- <i class="mr-2 fa-solid fa-right-from-bracket"></i> -->
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 512 512"
            class="w-6 h-6"
          >
            <path
              d="M377.9 105.9L500.7 228.7c7.2 7.2 11.3 17.1 11.3 27.3s-4.1 20.1-11.3 27.3L377.9 406.1c-6.4 6.4-15 9.9-24 9.9c-18.7 0-33.9-15.2-33.9-33.9l0-62.1-128 0c-17.7 0-32-14.3-32-32l0-64c0-17.7 14.3-32 32-32l128 0 0-62.1c0-18.7 15.2-33.9 33.9-33.9c9 0 17.6 3.6 24 9.9zM160 96L96 96c-17.7 0-32 14.3-32 32l0 256c0 17.7 14.3 32 32 32l64 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-64 0c-53 0-96-43-96-96L0 128C0 75 43 32 96 32l64 0c17.7 0 32 14.3 32 32s-14.3 32-32 32z"
            />
          </svg>
          Log Out
        </Button>
      </div>
    </aside>

    <!-- Main content area -->
    <div class="flex-1 p-4 md:pl-12">
      <slot />
    </div>
  </div>
</template>

<script setup>

import { Button } from '~/components/ui/button';

const twoFa = localStorage.getItem("2fa");
const sessionToken = localStorage.getItem("auth_token"); 
const sessionUrl = ref(String); 

onMounted(() => {
  fetchUrl()
}); 

async function logout() {
  // Borramos el token

  await fetch("http://localhost:5173/api-auth/logout", {
    method: "GET",
    credentials: "include",
  });

  await localStorage.removeItem("auth_token");

  window.location.href = "/";

}

async function fetchUrl(){
  const resp = await fetch("http://localhost:5173/api-users/redir", {
    method: "GET",
    headers:{
      Authorization: `Bearer ${sessionToken}`
    } 
  });

  const data = await resp.json(); 

  console.log("data: ", data); 

  sessionUrl.value = data.url; 

}

</script>
